/**
 * Which returns-accounting behaviour a return was created under.
 *
 * The RAC is a pure function of (document, settings, code) and its results are
 * recomputed rather than stored, which is what makes it authoritative for
 * historical returns — and also what makes changing it dangerous: without a
 * stamp, a code change silently reprices every return ever taken.
 *
 * So each behaviour change gets a version, and a return is stamped once with
 * the version it was created under. New behaviour reaches new returns only.
 *
 * Rules that keep this honest:
 *
 *   - Stamped once at creation, never updated. An edit must not move a return
 *     onto newer behaviour, or retroactive change comes straight back.
 *   - A draft stamps at creation and the return **inherits the draft's
 *     version**, not the current one. A draft started before a deploy and
 *     submitted after must keep its original pricing, or the shopper is quoted
 *     one number and charged another — the exact failure this exists to stop.
 *   - Absent means legacy. Nothing is ever backfilled: writing a version onto
 *     an old return would rewrite its history.
 *   - A version string is frozen once committed. If a release slips the label
 *     does not move; renaming it reinterprets everything already stamped.
 *   - The RAC reads only the stamp, never a flag service, so it stays pure and
 *     replayable offline.
 *
 * Values are `vYYYY-MM-DD` because lexicographic order is the comparison
 * mechanism — a single unpadded month would silently break every check. Two
 * changes on one day take a suffix (`v2026-09-22b`), which still sorts.
 *
 * The `v` says "label, not date" at a glance, which is the mistake this
 * comment spends a paragraph warning about. A constant prefix leaves the
 * ordering untouched.
 *
 * This is a version label, NOT a timestamp. It is not the return's date and
 * must never be compared against `createdAt`.
 */
export enum AccountingVersion {
  /**
   * Totals derive from the allocation engine rather than per-product rules.
   *
   * Behaviour-preserving by construction: the engine reproduces the
   * pre-allocation results exactly, with the difference verified by offline
   * shadow replay before this shipped. Nothing a shopper sees changes.
   *
   * Retire once no unprocessed return predates it.
   */
  "v2026-09-22" = "v2026-09-22",
  /** A same-product shop-now purchase settles item for item (RET1-2426), and a
   * purchase from the returned item's exchange group is the group's swap (RET1-3255). */
  "v2026-09-23" = "v2026-09-23",
}

/** Lexicographic, which the `vYYYY-MM-DD` shape is built for. Not exported:
 * a bare comparison says nothing about which behaviour is being tested. */
const atLeast = (version: AccountingVersion, floor: AccountingVersion) =>
  version >= floor;

/** RET1-2426: pair a same-product purchase with the cheapest unit it matches. */
export const pairsSameProductPurchases = (version: AccountingVersion) =>
  atLeast(version, AccountingVersion["v2026-09-23"]);

/** RET1-3255: pair a purchase from a returned unit's exchange group, on the
 * group's terms. Also gates the catalog lookup that finds the groups. */
export const pairsExchangeGroupPurchases = (
  version: AccountingVersion | null | undefined,
) => !!version && atLeast(version, AccountingVersion["v2026-09-23"]);
